/**
 * RFC 0345 3c: a document a shared session's agent wrote reaches the session's
 * members only once its owner explicitly approves. Transcript entries are
 * untrusted suggestions, not authenticated evidence of an agent write.
 *
 * Runs in the OWNER's window. A guest's World does not list the session as
 * theirs, so a guest is never asked and never grants.
 *
 * Automatic sharing (behind VITE_MOD_AUTO_SHARE_DOCUMENTS=1, off by default)
 * never reads the transcript: core's shareAgentWrittenDocuments grants only
 * when the owner's device signed the allowance in their own World and the
 * session's agent key signed a receipt for the write.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  type Allowance,
  documentsAwaitingShare,
  projectDocuments,
  type SessionDoc,
  type TranscriptMessage,
  type WorldDoc,
} from '@mod/core/model'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTabRepoBoot } from '../world/repo/TabRepoProvider'
import { useDocument } from '../world/repo/useDocument'
import { useSharingAccount } from './accountContext'
import { uniqueActiveInvites } from './inviteEntries'

/** Loaded on use: the sharing core is large and only an owner needs it. */
const sharingCore = () => import('@mod/core/sharing')

export interface PendingDocument {
  documentId: string
  title: string
}

/**
 * Candidates for the owner to inspect and explicitly approve.
 * Empty unless this World owns the session and has shared it with someone.
 */
export function documentApprovalState(input: {
  sessionId: string
  session: SessionDoc | undefined
  world: WorldDoc | undefined
  projectDoc: unknown
  transcript: TranscriptMessage[]
  now?: number
}): { pending: PendingDocument[]; allowed: boolean } {
  const { sessionId, session, world } = input
  const none = { pending: [], allowed: false }
  if (!session || !world) return none
  if (!world.privateSessions?.[sessionId]) return none
  if (typeof session.sharingGroupId !== 'string') return none
  if (!uniqueActiveInvites(world.sharingInvites, sessionId, input.now).length)
    return none
  const catalog = new Map(
    projectDocuments(input.projectDoc).map((d) => [d.id, d.title]),
  )
  const pending = documentsAwaitingShare(input.transcript, {
    sharedDocumentIds: session.sharedDocumentIds ?? [],
    projectDocumentIds: new Set(catalog.keys()),
  }).map((documentId) => ({
    documentId,
    title: catalog.get(documentId) ?? documentId,
  }))
  // Shared-session entries are editable by guests. Neither a transcript role
  // nor a legacy allowance authenticates the owner or the actual document write.
  // Even a private World allowance cannot authenticate a forged transcript.
  // Fail closed until BOTH have a verified, owner-specific provenance path.
  return { pending, allowed: false }
}

export type ApprovalChoice = 'once' | Allowance['scope']

/** Off in shipped config until the key-hosted agent's receipts are proved
 * end to end (RFC 0345 S5). */
export function autoShareDocumentsEnabled(): boolean {
  return import.meta.env.VITE_MOD_AUTO_SHARE_DOCUMENTS === '1'
}

/** Only an explicit owner action can grant a candidate document. */
export function useDocumentApprovals(input: {
  sessionId: string
  projectDoc: unknown
  transcript: TranscriptMessage[]
  /** Defaults to autoShareDocumentsEnabled(). */
  autoShare?: boolean
  /** Tests hand in the sharing core's entry points; the window loads it. */
  core?: () => Promise<
    Pick<
      Awaited<ReturnType<typeof sharingCore>>,
      | 'grantApprovedDocumentToSession'
      | 'shareAgentWrittenDocuments'
      | 'addDocumentShareAllowance'
    >
  >
}): {
  pending: PendingDocument[]
  choose: (documentId: string, choice: ApprovalChoice) => Promise<void>
  error: string
} {
  const { sessionId } = input
  const { sharing } = useTabRepoBoot()
  const account = useSharingAccount()
  const worldId = account?.opened.location.worldId
  const personId = account?.opened.location.personId
  const autoShare = input.autoShare ?? autoShareDocumentsEnabled()
  const load = input.core ?? sharingCore
  const [world] = useDocument<WorldDoc>(worldId as AnyDocumentId | undefined)
  const [session] = useDocument<SessionDoc>(sessionId as AnyDocumentId)
  const { pending } = useMemo(
    () =>
      sharing
        ? documentApprovalState({
            sessionId,
            session,
            world,
            projectDoc: input.projectDoc,
            transcript: input.transcript,
          })
        : { pending: [], allowed: false },
    [sharing, sessionId, session, world, input.projectDoc, input.transcript],
  )
  const [error, setError] = useState('')
  const granting = useRef(new Set<string>())

  const grant = useCallback(
    async (documentId: string) => {
      if (!sharing || granting.current.has(documentId)) return
      granting.current.add(documentId)
      try {
        const { grantApprovedDocumentToSession } = await load()
        await grantApprovedDocumentToSession(sharing, sessionId, documentId)
        setError('')
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not share it')
      } finally {
        granting.current.delete(documentId)
      }
    },
    [sharing, sessionId, load],
  )

  // The owner's window is the only place the owner's device signs. A guest
  // never gets here: their World does not hold the session privately.
  const owns = Boolean(world?.privateSessions?.[sessionId])
  const writes = session?.documentWrites
  const sweeping = useRef(false)
  useEffect(() => {
    if (!autoShare || !sharing || !owns || !worldId || !personId) return
    if (!Array.isArray(writes) || !writes.length || sweeping.current) return
    sweeping.current = true
    void (async () => {
      try {
        const { shareAgentWrittenDocuments } = await load()
        await shareAgentWrittenDocuments(sharing, {
          owner: personId,
          worldId,
          sessionId,
        })
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not share it')
      } finally {
        sweeping.current = false
      }
    })()
  }, [autoShare, sharing, owns, worldId, personId, sessionId, writes, load])

  const choose = useCallback(
    async (documentId: string, choice: ApprovalChoice) => {
      if (!sharing) return
      // A session allowance lives where its guests can restore it after the
      // owner deletes it, so it is never issued.
      if (choice === 'session' || (choice !== 'once' && !autoShare)) {
        setError(
          'Always allow is unavailable until document writes can be verified. Approve this document instead.',
        )
        return
      }
      if (!pending.some((candidate) => candidate.documentId === documentId)) {
        setError('This document is not awaiting your approval')
        return
      }
      if (choice === 'everywhere') {
        if (!worldId || !personId) {
          setError('Sign in again to allow this')
          return
        }
        try {
          const { addDocumentShareAllowance } = await load()
          await addDocumentShareAllowance(sharing, {
            owner: personId,
            worldId,
          })
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Could not allow it')
          return
        }
      }
      await grant(documentId)
    },
    [sharing, pending, grant, autoShare, worldId, personId, load],
  )

  return { pending, choose, error }
}
